import React, { useEffect, useRef } from 'react';
import './OtpInput.css';

interface OtpInputProps {
    value: string;
    onChange: (value: string) => void;
    length?: number;
    autoFocus?: boolean;
    hasError?: boolean;
    disabled?: boolean;
}

/**
 * One box per digit. Digits only, auto-advances on input, Backspace moves back,
 * arrow keys move between boxes and pasting a full code fills all boxes.
 */
const OtpInput: React.FC<OtpInputProps> = ({ value, onChange, length = 6, autoFocus, hasError, disabled }) => {
    const inputs = useRef<(HTMLInputElement | null)[]>([]);
    const digits = Array.from({ length }, (_, i) => value[i] || '');

    useEffect(() => {
        if (autoFocus) {
            inputs.current[0]?.focus();
        }
    }, [autoFocus]);

    const focusBox = (index: number) => {
        const box = inputs.current[Math.max(0, Math.min(length - 1, index))];
        box?.focus();
        box?.select();
    };

    // Writes digits starting at `index` (supports typing one digit or pasting several).
    const setDigits = (index: number, text: string) => {
        const entered = text.replace(/\D/g, '');
        if (!entered) {
            return;
        }
        const next = [...digits];
        let position = index;
        for (const digit of entered) {
            if (position >= length) {
                break;
            }
            next[position++] = digit;
        }
        onChange(next.join(''));
        focusBox(position);
    };

    const clearDigit = (index: number) => {
        const next = [...digits];
        next[index] = '';
        // Keep the value contiguous: shift following digits left.
        onChange((next.slice(0, index).join('') + next.slice(index + 1).join('')).slice(0, length));
    };

    const onKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace') {
            event.preventDefault();
            if (digits[index]) {
                clearDigit(index);
            }
            else if (index > 0) {
                clearDigit(index - 1);
                focusBox(index - 1);
            }
        }
        else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            focusBox(index - 1);
        }
        else if (event.key === 'ArrowRight') {
            event.preventDefault();
            focusBox(index + 1);
        }
    };

    return (
        <div className={`otp-input ${hasError ? 'has-error' : ''}`}>
            {digits.map((digit, index) =>
                <input
                    key={index}
                    ref={element => { inputs.current[index] = element; }}
                    className='otp-box'
                    type='text'
                    inputMode='numeric'
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    aria-label={`Digit ${index + 1}`}
                    maxLength={length}
                    value={digit}
                    disabled={disabled}
                    onFocus={event => event.target.select()}
                    onChange={event => {
                        const text = event.target.value;
                        if (digit && text.length === digit.length + 1) {
                            // Typed into a filled box: keep only the new digit (caret before or after the old one).
                            setDigits(index, text.startsWith(digit) ? text.slice(digit.length) : text.slice(0, 1));
                        }
                        else {
                            // Single digit, or a full code from autofill / one-time-code.
                            setDigits(index, text);
                        }
                    }}
                    onKeyDown={event => onKeyDown(index, event)}
                    onPaste={event => {
                        event.preventDefault();
                        setDigits(index, event.clipboardData.getData('text'));
                    }}
                />
            )}
        </div>
    );
};

export default OtpInput;
